import { create } from 'zustand'
import { clampHalfY, type ViewIndex, type ViewMode, type ViewState } from '../scene/camera'
import type { RotationSteps, WallSide } from './document'

export type Tool =
  | { kind: 'select' }
  | { kind: 'place'; assetId: string; variant?: string; rotation: RotationSteps }

export interface GhostState {
  x: number
  z: number
  y: number
  rotation: RotationSteps
  wall?: WallSide
  valid: boolean
}

/**
 * In-flight pose of the item being dragged. It deliberately lives outside the
 * document: writing 60 poses a second into the history store would evict every
 * real entry inside a second. The document is written once, on drop.
 */
export interface DragPose {
  uid: string
  x: number
  z: number
  y: number
  rotation: RotationSteps
  wall?: WallSide
  valid: boolean
}

const DEFAULT_VIEW: ViewState = {
  mode: 'iso',
  viewIndex: 0,
  cx: 0,
  cy: 1,
  cz: 0,
  halfY: 4.4,
}

interface EditorState {
  tool: Tool
  selectedId: string | null
  hoveredId: string | null
  ghost: GhostState | null
  dragPose: DragPose | null
  isDragging: boolean
  catalogFilter: string
  /** Bumped to ask the camera rig for a fresh auto-fit. */
  fitRequest: number
  view: ViewState

  armPlacement: (assetId: string, variant?: string) => void
  cancelPlacement: () => void
  rotateTool: (delta: number) => void
  select: (uid: string | null) => void
  hover: (uid: string | null) => void
  setGhost: (ghost: GhostState | null) => void
  setDragPose: (pose: DragPose | null) => void
  setDragging: (dragging: boolean) => void
  setCatalogFilter: (filter: string) => void
  requestFit: () => void

  setView: (patch: Partial<ViewState>) => void
  rotateView: (delta: number) => void
  setMode: (mode: ViewMode) => void
  zoomBy: (factor: number) => void
  panBy: (dx: number, dz: number) => void
}

export const useEditor = create<EditorState>((set, get) => ({
  tool: { kind: 'select' },
  selectedId: null,
  hoveredId: null,
  ghost: null,
  dragPose: null,
  isDragging: false,
  catalogFilter: '',
  fitRequest: 0,
  view: DEFAULT_VIEW,

  armPlacement: (assetId, variant) =>
    set({ tool: { kind: 'place', assetId, variant, rotation: 0 }, selectedId: null, ghost: null }),

  cancelPlacement: () => set({ tool: { kind: 'select' }, ghost: null }),

  rotateTool: (delta) => {
    const { tool } = get()
    if (tool.kind !== 'place') return
    const rotation = (((tool.rotation + delta) % 4) + 4) % 4
    set({ tool: { ...tool, rotation: rotation as RotationSteps } })
  },

  select: (uid) => set({ selectedId: uid }),
  hover: (uid) => set({ hoveredId: uid }),
  setGhost: (ghost) => set({ ghost }),
  setDragPose: (dragPose) => set({ dragPose }),
  setDragging: (isDragging) => set({ isDragging }),
  setCatalogFilter: (catalogFilter) => set({ catalogFilter }),
  requestFit: () => set({ fitRequest: get().fitRequest + 1 }),

  setView: (patch) => set({ view: { ...get().view, ...patch } }),

  rotateView: (delta) => {
    const { view } = get()
    const viewIndex = ((((view.viewIndex + delta) % 4) + 4) % 4) as ViewIndex
    set({ view: { ...view, viewIndex } })
  },

  setMode: (mode) => set({ view: { ...get().view, mode } }),

  zoomBy: (factor) => {
    const { view } = get()
    set({ view: { ...view, halfY: clampHalfY(view.halfY * factor) } })
  },

  panBy: (dx, dz) => {
    const { view } = get()
    set({ view: { ...view, cx: view.cx + dx, cz: view.cz + dz } })
  },
}))
